/**
 * Commission invoice payments: map each payment to an installment line (by order).
 */

export function getCommissionPayableLines(lineItems = []) {
  return (Array.isArray(lineItems) ? lineItems : [])
    .filter(
      (line) =>
        line?.commission_installment_id ||
        line?.commissionInstallmentId ||
        line?.bonus_installment_id ||
        line?.bonusInstallmentId,
    )
    .sort(
      (left, right) =>
        Number(left?.line_number ?? left?.lineNumber ?? 0) -
        Number(right?.line_number ?? right?.lineNumber ?? 0),
    );
}

export function resolveCommissionPaymentTargetForIndex(lineItems = [], paymentIndex = 0) {
  const payableLines = getCommissionPayableLines(lineItems);
  const line = payableLines[paymentIndex] || null;
  if (!line) return {};

  return {
    finance_invoice_item_id:
      line.id ?? line.line_item_id ?? line.finance_invoice_item_id ?? null,
    commission_installment_id:
      line.commission_installment_id ?? line.commissionInstallmentId ?? null,
    bonus_installment_id: line.bonus_installment_id ?? line.bonusInstallmentId ?? null,
    line_number: line.line_number ?? line.lineNumber ?? paymentIndex + 1,
  };
}

export function countCommissionInvoicePayments(transactions = []) {
  return (Array.isArray(transactions) ? transactions : []).filter((record) => {
    const type = String(
      record?.type ?? record?.transaction_type ?? "",
    ).toLowerCase();
    return type === "payment";
  }).length;
}

export function buildCommissionPaymentRecord(payment = {}, target = {}) {
  return {
    transaction_type: payment.transaction_type || "payment",
    amount: Number(payment.amount) || 0,
    payment_method: payment.payment_method || "bank",
    transaction_date:
      payment.transaction_date || payment.payment_date || "",
    payment_date:
      payment.payment_date || payment.transaction_date || "",
    reference: String(payment.reference || "").trim(),
    description: String(payment.description || "").trim() || "Payment received",
    finance_invoice_item_id: target.finance_invoice_item_id ?? null,
    commission_installment_id: target.commission_installment_id ?? null,
    bonus_installment_id: target.bonus_installment_id ?? null,
    line_number: target.line_number ?? null,
  };
}

export function mapCommissionPaymentsFromPayload(payload = {}, lineItems = []) {
  const payableLines = getCommissionPayableLines(
    lineItems.length ? lineItems : payload?.line_items || [],
  );

  const rawList = Array.isArray(payload?.payments)
    ? payload.payments
    : payload?.payment && Number(payload.payment.amount) > 0
      ? [payload.payment]
      : [];

  return rawList
    .filter((row) => Number(row?.amount) > 0)
    .map((row, index) =>
      buildCommissionPaymentRecord(
        row,
        resolveCommissionPaymentTargetForIndex(
          payableLines.length ? payableLines : lineItems,
          index,
        ),
      ),
    );
}

export function getCommissionPaymentTargetLabel(target = {}) {
  if (target.commission_installment_id) {
    return `Tuition installment #${target.commission_installment_id}`;
  }
  if (target.bonus_installment_id) {
    return `Bonus installment #${target.bonus_installment_id}`;
  }
  if (target.line_number) {
    return `Line ${target.line_number}`;
  }
  return "";
}
